import { Region } from './../../model/region.interface';
import { SiaWavesurferMarker } from './../model/sia-wavesurfer-marker.interface';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as WaveSurfer from 'wavesurfer.js';
import Regions from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugin/wavesurfer.timeline.min.js';
import MarkersPlugin from 'wavesurfer.js/src/plugin/markers/index.js';

import { TrackCommand, TrackCommandType } from './../../model/track-command.interface';
import TimelineUtils from './timeline-utils';
import { bufferToWave, copy, cut, cutAudioInSpecificTimeInSeconds, pasteAudio } from './utils/wavesurfer-operation';

@Component({
  selector: 'app-sia-wave-track',
  templateUrl: './sia-wave-track.component.html',
  styleUrls: ['./sia-wave-track.component.scss']
})
export class SiaWaveTrackComponent implements OnInit, AfterViewInit, OnDestroy {


  @Input() loadData: Observable<string | Blob>;

  @Input() elementId: string;

  @Input() isCreator: boolean;

  @Input() events: Observable<TrackCommand>;

  wavesurfer: WaveSurfer;
  waveTimeline: TimelinePlugin;

  status: 'playing' | 'paused' | 'stopped' = 'stopped';

  @Output() stopPlayingEvent = new EventEmitter<void>();

  @Output() pauseEvent = new EventEmitter<void>();

  @Output() duration = new EventEmitter<string>();

  @Output() currentTimeEvent = new EventEmitter<number>();

  @Output() regionSelected = new EventEmitter<any>();

  regionSavedStart;
  markers: SiaWavesurferMarker[] = [];
  scale = 0;
  cutSelection: any;


  constructor(private changeDetector: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.events?.subscribe((message: TrackCommand) => {
      switch (message.command) {
        case TrackCommandType.START:
          this.playTrack();
          break;
        case TrackCommandType.PAUSE:
          this.pauseTrack();
          break;
        case TrackCommandType.STOP:
          this.stopTrack();
          break;
        case TrackCommandType.CUT:
          this.cutTrack();
          break;
        case TrackCommandType.PASTE:
          this.pasteTrack();
          break;
        case TrackCommandType.ZOOM:
          this.zoomTrack(message.options);
          break;
        case TrackCommandType.CLEAR_REGION:
          this.clearRegion();
          break;
        case TrackCommandType.BACKWARD:
          this.backward(message.options);
          break;
        case TrackCommandType.FORWARD:
          this.forward(message.options);
          break;
        case TrackCommandType.CHANGE_REGION:
          this.changeRegion(message.options);
          break;
        case TrackCommandType.CLEAR_WAVESURFER:
          this.clearWaveSurfer();
          break;
        case TrackCommandType.MOVE:
          this.move(message.options.progress);
          break;
        case TrackCommandType.ADD_MARKER:
          this.addMarker(message.options);
      }
    });

    this.loadData.subscribe((value: string | Blob) => {
      if (value != null && value !== '') {
        this.loadWaveSurfer(value);
      } else {
        this.clearWaveSurfer();
      }
    });

  }

  ngOnDestroy(): void {
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
  }

  ngAfterViewInit(): void {
    this.initializeWaveSurfer(this.elementId);
  }

  playTrack(): void {
    if (this.hasRegionAdded()) {
      const regions = Object.keys(this.wavesurfer.regions.list);
      regions.forEach(id => {
        const region = this.wavesurfer.regions.list[id];
        // If the tracker is inside the region, it calls the region.play
        if (this.regionSavedStart != null && this.regionSavedStart >= region.start && this.regionSavedStart <= region.end) {
          region.play(this.regionSavedStart);
        } else { // Otherwise, it calls wavesurfer.play
          this.wavesurfer.play(this.regionSavedStart != null ? this.regionSavedStart : region.start);
        }
      });

    } else {

      this.wavesurfer.play();
    }
  }

  pauseTrack(): void {
    if (this.hasRegionAdded()) {
      this.regionSavedStart = this.wavesurfer.getCurrentTime();
    }
    this.wavesurfer.pause();
  }

  stopTrack(): void {
    this.wavesurfer.stop();
    this.changeDetector.detectChanges();
  }

  pauseManually(): void {
    this.pauseTrack();
    this.pauseEvent.emit();
  }

  zoomTrack(zoomLevel: number): void {
    this.wavesurfer.zoom(zoomLevel);
  }

  clearRegion(): void {
    this.wavesurfer?.regions && this.wavesurfer.regions.clear();
    this.regionSavedStart = null;
  }

  getDuration(): void {
    this.duration.emit(this.wavesurfer.getDuration());
  }

  onScroll(event): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.deltaY * -0.01 <= 0) {
      this.scale = this.scale < 1 ? this.scale = 0 : this.scale -= environment.wavesurferZoom;

    } else {
      this.scale += environment.wavesurferZoom;
    }
    this.zoomTrack(this.scale);
  }

  copyAndReturnSegment(region) {
    this.cutSelection = copy(region, this.wavesurfer);
  }
  cutAndReturnAudioBuffer(region) {
    const k = cut(region, this.wavesurfer);
    const newAudioBuffer = k.newAudioBuffer;
    this.cutSelection = k.cutSelection;
    this.wavesurfer.loadDecodedBuffer(newAudioBuffer);

  }

  pasteAndReturnAudioBuffer() {
    const newAudioBuffer = pasteAudio(this.cutSelection, this.wavesurfer);
    this.wavesurfer.loadDecodedBuffer(newAudioBuffer);
  }

  /**
   *
   * Private Methods
   */

  private cutTrack() {
    this.cutAndReturnAudioBuffer(this.wavesurfer.regions.list[this.getIdNameRegion()]);

  }

  private pasteTrack() {
    this.pasteAndReturnAudioBuffer();
  }

  private clearWaveSurfer(): void {
    this.wavesurfer?.empty();
    this.clearRegion();
  }

  private move(progress: number): void {
    this.stopTrack();
    this.wavesurfer.skip(progress);
  }

  private initializeWaveSurfer(containerId: string): void {
    this.waveTimeline = TimelinePlugin.create({
      container: '#wave-timeline',
      formatTimeCallback: TimelineUtils.formatTimeCallback,
      timeInterval: TimelineUtils.timeInterval,
      primaryLabelInterval: null,
      secondaryLabelInterval: TimelineUtils.secondaryLabelInterval,
    });
    this.wavesurfer = WaveSurfer.create({
      container: `#${containerId}`,
      waveColor: '#c6002b',
      progressColor: '#c6002b',
      responsive: true,
      normalize: true,
      plugins: [
        this.waveTimeline,
        Regions.create({
          maxRegions: 1,
          regions: [],
          dragSelection: {
            slop: 1
          }
        }),
        MarkersPlugin.create({
          markers: [],
        })
      ]
    });

    this.wavesurfer.on('ready', () => {
      this.stopPlayingEvent.emit();
      this.regionSavedStart = null;
      // this.clearOrAddMarkers();

      if (this.markers.length > 0) {
        this.wavesurfer.addRegion({ start: this.markers[0].time, end: this.markers[1].time });
        this.markers = [];

        const regionName = this.getIdNameRegion();
        /**
         * We are cutting audio because the audio is longer than the normal
         */
        const cutSegment = cutAudioInSpecificTimeInSeconds(this.wavesurfer.regions.list[regionName], this.wavesurfer);
        this.wavesurfer.loadDecodedBuffer(cutSegment);
        this.wavesurfer.clearRegions();
        this.getDuration();
        this.wavesurfer.enableDragSelection({
          drag: true,
          resize: true,
          loop: true,
        });

      }

    });

    /**
     * This always update the regionSavedStart when user clicks on the wavesurfer
     */
    this.wavesurfer.on('seek', (position: number) => {
      // position is a percentage
      this.regionSavedStart = this.wavesurfer.getDuration() * position;
    });

    this.wavesurfer.on('region-update-end', ({start, end}) => this.onRegionChange({start, end}));

    this.wavesurfer.on('region-play', (region) => {
      region.once('out', () => {
        // Only pause the audio if it started to play inside the region in order to allow playing when user clicks outside the region
        //  and clicks to play the audio from where he clicked
        if (this.regionSavedStart >= region.start && this.regionSavedStart <= region.end) {
          this.wavesurfer.play(region.start);
          this.regionSavedStart = null;
          this.pauseEvent.emit();
        }
      });
    });

    this.wavesurfer.on('audioprocess', (value) => {
      this.currentTimeEvent.emit(value);
    });

  }

  private changeRegion(region): void {
    if (this.hasRegionAdded()) {
      Object.keys(this.wavesurfer.regions.list).forEach(id => {
        const regionIn = this.wavesurfer.regions.list[id];
        const start = region.start;
        const end = region.end;
        regionIn.update({ start, end });
      });
    } else {
      this.wavesurfer.addRegion(region);
    }

    this.onRegionChange(region);
  }

  private saveRegions = () => {
    this.regionSelected.emit(this.wavesurfer.regions.list);
  }

  private loadWaveSurfer(value: string | Blob): void {
    if (value instanceof Blob) {
      this.wavesurfer?.loadBlob(value);
    } else {
      this.wavesurfer?.load(value);
    }
  }

  private backward(skip): void {
    this.wavesurfer.skipBackward(skip);
  }

  private forward(skip): void {
    this.wavesurfer.skipForward(skip);
  }

  private hasRegionAdded(): number {
    return Object.keys(this.wavesurfer.regions.list).length;
  }

  private addMarker = (marker: SiaWavesurferMarker) => {
    if (this.markers.length >= 2) {
      this.markers = [];
    }
    this.markers.push(marker);
  }

  private hasMarkersAdded(): number {
    return this.wavesurfer.markers.markers.length;
  }

  private clearOrAddMarkers = () => {
    if (this.hasMarkersAdded() >= 2) {
      this.wavesurfer.markers.clear();
    }
    this.markers.forEach(marker => {
      this.wavesurfer.markers.add(marker);
    });
  }

  private getIdNameRegion(): string {
    return Object.keys(this.wavesurfer.regions.list)[0];
  }

  /**
   * Makes the regionSavedStart to be equal to the start of the selected region when a region change occurs
   * @param region after it changes
   */
  private onRegionChange(region: Region) {
    this.regionSavedStart = region.start;
    // Move play head to the beginning of the region
    this.wavesurfer.seekTo(region.start / this.wavesurfer.getDuration());
    this.saveRegions();
  }

}
